import { ResponsiveBar } from '@nivo/bar';
import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartInterval, useUserSessionQuery } from '../../../../../services';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, getBarsLayer, getBestValues } from '../../helpers';
import { UserProps } from '../User';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-session-by-day-of-week';

export const UserSessionByDay: FunctionComponent<UserProps> = memo((userProps) => {
  const { t } = useTranslation();

  const { data: userSessionByDay, isFetching: isFetchingUserSessionByDay } = useUserSessionQuery({
    ...userProps,
    interval: AnalyticChartInterval.DAY_OF_WEEK,
  });

  const formatter = useCallback((value: string) => t(`daysOfWeek.${value.toLowerCase()}`), [t]);

  const renderChart = useMemo(() => {
    if (isFetchingUserSessionByDay) {
      return <Loader />;
    }

    if (!userSessionByDay) {
      return null;
    }

    const { datasets, intervals } = userSessionByDay;

    if (!datasets.length) {
      return <div className={classes['analytics__chart-no-records']}>{t('common.no-records')}</div>;
    }

    const values = datasets[0].data;

    const data = values.map((value, index) => {
      return { interval: intervals[index], value };
    });

    const bestValues = getBestValues({ values, intervals, formatter });

    return (
      <>
        {bestValues && (
          <div className={classes['analytics__chart-best']}>
            <div className={classes['analytics__chart-best-label']}>
              {t('userSessionByDayOfWeek.best-label')}
            </div>
            <div className={classes['analytics__chart-best-value']}>{bestValues}</div>
          </div>
        )}
        <div className={classes['analytics__chart-height']}>
          <ResponsiveBar
            ariaLabel={t(TYPE_LOCALISE_KEY)}
            margin={{ top: 40, right: 40, bottom: 50, left: 40 }}
            padding={0.5}
            borderRadius={4}
            tooltip={() => <></>}
            data={data}
            keys={['value']}
            indexBy={'interval'}
            colors={[COLORS[0]]}
            axisLeft={{ format: (value) => (value === Math.floor(value) ? value : '') }}
            axisBottom={{ format: (value) => formatter(value) }}
            label={''}
            layers={['grid', 'axes', 'bars', ({ bars }) => getBarsLayer({ bars })]}
          />
        </div>
      </>
    );
  }, [formatter, isFetchingUserSessionByDay, t, userSessionByDay]);
  return (
    <ChartWrapper
      typeLocaliseKey={TYPE_LOCALISE_KEY}
      infoLocaliseKey={'userSessionByDayOfWeek.info'}
    >
      {renderChart}
    </ChartWrapper>
  );
});
